import { Extension, InputRule, type Editor, type Range } from '@tiptap/core';
import { PluginKey, TextSelection } from '@tiptap/pm/state';
import Suggestion from '@tiptap/suggestion';
import { pickFiles } from './files';
import {
  AlarmClock,
  CalendarDays,
  ChevronsDownUp,
  Code2,
  Columns2,
  Columns3,
  FileInput,
  FilePlus2,
  FileText,
  Hash,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  ImagePlus,
  Paperclip,
  Info,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Sparkles,
  Table,
  Workflow,
  LayoutTemplate,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { toIsoDate } from '@nexus/core';
import type { EditorHost } from './host';
import { popupRenderer, SuggestionMenu, type MenuItem } from './popup';

interface MenuProps {
  items: MenuItem[];
  empty: string;
}

const menu = (host: EditorHost) =>
  popupRenderer<MenuProps>(SuggestionMenu, (p) => ({
    items: p.items as MenuItem[],
    empty: host.t('editor.noResults'),
  }));

/** What may come before a trigger character: a space, or a no-break space. */
const SPACES = [' ', '\u00a0'];

// ---- slash commands ------------------------------------------------------------

interface SlashCommand {
  id: string;
  icon: ReactNode;
  keywords: string;
  run: (editor: Editor, range: Range) => void;
  /** Only offered when this holds (e.g. the cursor is in a task). */
  when?: (editor: Editor) => boolean;
}

/** Id of the task item at the cursor, if any. */
function taskIdAtCursor(editor: Editor): string | null {
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type.name === 'taskItem') return (node.attrs.taskId as string | null) ?? null;
  }
  return null;
}

/** Insert empty columns and put the cursor in the first one. */
function insertColumns(chain: ReturnType<Editor['chain']>, n: number) {
  chain
    .insertContent(columns(n))
    .command(({ tr }) => {
      let at = -1;
      const cursor = tr.selection.from;
      tr.doc.descendants((node, pos) => {
        if (node.type.name === 'columns' && pos <= cursor) at = pos;
      });
      if (at >= 0) tr.setSelection(TextSelection.create(tr.doc, at + 3));
      return true;
    })
    .run();
}

/** Empty columns, side by side. */
const columns = (n: number) => ({
  type: 'columns',
  content: Array.from({ length: n }, () => ({ type: 'column', content: [{ type: 'paragraph' }] })),
});

function slashCommands(host: EditorHost): SlashCommand[] {
  const chain = (e: Editor, r: Range) => e.chain().focus().deleteRange(r);
  const commands: SlashCommand[] = [
    {
      id: 'text',
      icon: <Pilcrow size={16} />,
      keywords: 'text tekst paragraph',
      run: (e, r) => chain(e, r).setParagraph().run(),
    },
    {
      id: 'h1',
      icon: <Heading1 size={16} />,
      keywords: 'heading kop titel h1',
      run: (e, r) => chain(e, r).setHeading({ level: 1 }).run(),
    },
    {
      id: 'h2',
      icon: <Heading2 size={16} />,
      keywords: 'heading kop h2',
      run: (e, r) => chain(e, r).setHeading({ level: 2 }).run(),
    },
    {
      id: 'h3',
      icon: <Heading3 size={16} />,
      keywords: 'heading kop h3',
      run: (e, r) => chain(e, r).setHeading({ level: 3 }).run(),
    },
    {
      id: 'bullets',
      icon: <List size={16} />,
      keywords: 'bullet list lijst opsomming',
      run: (e, r) => chain(e, r).toggleBulletList().run(),
    },
    {
      id: 'numbered',
      icon: <ListOrdered size={16} />,
      keywords: 'numbered ordered genummerd lijst',
      run: (e, r) => chain(e, r).toggleOrderedList().run(),
    },
    {
      id: 'tasks',
      icon: <ListChecks size={16} />,
      keywords: 'task todo taak checkbox',
      run: (e, r) => chain(e, r).toggleTaskList().run(),
    },
    {
      id: 'due',
      icon: <CalendarDays size={16} />,
      keywords: 'deadline datum due vervaldatum 📅',
      run: (e, r) =>
        chain(e, r)
          .insertContent(`📅 ${toIsoDate(new Date())}`)
          .run(),
    },
    {
      id: 'reminder',
      icon: <AlarmClock size={16} />,
      keywords: 'herinnering reminder alarm melding ⏰',
      run: (e, r) => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        chain(e, r)
          .insertContent(`⏰ ${toIsoDate(tomorrow)} 09:00`)
          .run();
      },
    },
    {
      id: 'quote',
      icon: <Quote size={16} />,
      keywords: 'quote citaat blockquote',
      run: (e, r) => chain(e, r).toggleBlockquote().run(),
    },
    {
      id: 'callout',
      icon: <Info size={16} />,
      keywords: 'callout info tip note waarschuwing',
      run: (e, r) =>
        chain(e, r)
          .insertContent({
            type: 'callout',
            attrs: { kind: 'info' },
            content: [{ type: 'paragraph' }],
          })
          .run(),
    },
    {
      id: 'code',
      icon: <Code2 size={16} />,
      keywords: 'code codeblock',
      run: (e, r) => chain(e, r).toggleCodeBlock().run(),
    },
    {
      id: 'mermaid',
      icon: <Workflow size={16} />,
      keywords: 'mermaid diagram flowchart schema',
      run: (e, r) =>
        chain(e, r)
          .insertContent({
            type: 'codeBlock',
            attrs: { language: 'mermaid' },
            content: [{ type: 'text', text: 'graph TD\n  A --> B' }],
          })
          .run(),
    },
    {
      id: 'table',
      icon: <Table size={16} />,
      keywords: 'table tabel',
      run: (e, r) => chain(e, r).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
    },
    {
      id: 'divider',
      icon: <Minus size={16} />,
      keywords: 'divider hr scheidingslijn lijn',
      run: (e, r) => chain(e, r).setHorizontalRule().run(),
    },
    {
      id: 'link',
      icon: <Link2 size={16} />,
      keywords: 'link page pagina wikilink',
      run: (e, r) => chain(e, r).insertContent('[[').run(),
    },
    {
      id: 'h4',
      icon: <Heading4 size={16} />,
      keywords: 'h4 kop heading 4',
      run: (e, r) => chain(e, r).setHeading({ level: 4 }).run(),
    },
    {
      id: 'toggle',
      icon: <ChevronsDownUp size={16} />,
      keywords: 'toggle inklapblok uitklappen inklappen details',
      run: (e, r) =>
        chain(e, r)
          .insertContent({
            type: 'callout',
            attrs: { kind: 'toggle', fold: '+', title: '' },
            content: [{ type: 'paragraph' }],
          })
          .run(),
    },
    {
      id: 'columns2',
      icon: <Columns2 size={16} />,
      keywords: 'kolommen columns 2 twee naast elkaar',
      run: (e, r) => insertColumns(chain(e, r), 2),
    },
    {
      id: 'columns3',
      icon: <Columns3 size={16} />,
      keywords: 'kolommen columns 3 drie naast elkaar',
      run: (e, r) => insertColumns(chain(e, r), 3),
    },
    {
      id: 'embed',
      icon: <FileInput size={16} />,
      keywords: 'embed insluiten pagina',
      run: (e, r) => chain(e, r).insertContent('![[').run(),
    },
  ];
  if (host.uploadFile)
    commands.push(
      {
        id: 'image',
        icon: <ImagePlus size={16} />,
        keywords: 'afbeelding image foto picture plaatje',
        run: (e, r) => {
          chain(e, r).run();
          pickFiles(e, host, 'image/*');
        },
      },
      {
        id: 'file',
        icon: <Paperclip size={16} />,
        keywords: 'bestand file bijlage attachment pdf upload',
        run: (e, r) => {
          chain(e, r).run();
          pickFiles(e, host, '');
        },
      },
    );
  if (host.insertTemplate)
    commands.push({
      id: 'template',
      icon: <LayoutTemplate size={16} />,
      keywords: 'sjabloon template invoegen insert',
      run: (e, r) => {
        chain(e, r).run();
        host.insertTemplate?.(e);
      },
    });
  if (host.promoteTask)
    commands.push({
      id: 'taskPage',
      icon: <FilePlus2 size={16} />,
      keywords: 'taakpagina task page pagina omzetten promote',
      when: (e) => !!taskIdAtCursor(e),
      run: (e, r) => {
        const id = taskIdAtCursor(e);
        chain(e, r).run();
        if (id) host.promoteTask?.(id);
      },
    });
  if (host.aiWrite)
    commands.unshift({
      id: 'ai',
      icon: <Sparkles size={16} />,
      keywords: 'ai claude schrijven write schrijfhulp samenvatten',
      run: (e, r) => {
        chain(e, r).run();
        host.aiWrite?.(e);
      },
    });
  return commands.map((c) => ({
    ...c,
    keywords: `${host.t(`editor.slash.${c.id}`)} ${c.keywords}`.toLowerCase(),
  }));
}

export function createSlashCommands(host: EditorHost) {
  return Extension.create({
    name: 'slashCommands',
    addProseMirrorPlugins() {
      const commands = slashCommands(host);
      return [
        Suggestion<MenuItem>({
          editor: this.editor,
          pluginKey: new PluginKey('slashCommands'),
          char: '/',
          // "/kop 1" — allow spaces, but stop once nothing matches any more.
          allowSpaces: true,
          // After a highlighted date the browser types a no-break space: accept both.
          allowedPrefixes: SPACES,
          shouldShow: ({ query }) => commands.some((c) => c.keywords.includes(query.toLowerCase())),
          allow: ({ state, range }) => {
            const $from = state.doc.resolve(range.from);
            return !$from.parent.type.spec.code;
          },
          items: ({ query, editor }) => {
            const q = query.toLowerCase();
            return commands
              .filter((c) => (!q || c.keywords.includes(q)) && (!c.when || c.when(editor)))
              .map((c) => ({
                id: c.id,
                label: host.t(`editor.slash.${c.id}`),
                icon: c.icon,
                run: () => {
                  const { state } = editor;
                  const range = {
                    from: state.selection.from - query.length - 1,
                    to: state.selection.from,
                  };
                  c.run(editor, range);
                },
              }));
          },
          render: menu(host),
        }),
      ];
    },
  });
}

// ---- [[wikilinks]] and ![[embeds]] ---------------------------------------------

function insertLink(editor: Editor, range: Range, page: { id: string; title: string }) {
  const before = editor.state.doc.textBetween(Math.max(0, range.from - 1), range.from);
  if (before === '!') {
    editor
      .chain()
      .focus()
      .deleteRange({ from: range.from - 1, to: range.to })
      .insertContent({ type: 'embed', attrs: { pageId: page.id, target: page.title } })
      .run();
    return;
  }
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent([
      { type: 'wikiLink', attrs: { pageId: page.id, target: page.title } },
      { type: 'text', text: ' ' },
    ])
    .run();
}

export function createWikiLinkSuggest(host: EditorHost) {
  return Extension.create({
    name: 'wikiLinkSuggest',
    addProseMirrorPlugins() {
      return [
        Suggestion<MenuItem>({
          editor: this.editor,
          pluginKey: new PluginKey('wikiLinkSuggest'),
          char: '[[',
          allowSpaces: true,
          allowedPrefixes: null,
          items: ({ query, editor }) => {
            const q = query.replace(/\]+$/, '');
            const range = () => ({
              from: editor.state.selection.from - query.length - 2,
              to: editor.state.selection.from,
            });
            const items: MenuItem[] = host.searchPages(q, 8).map((p) => ({
              id: p.id,
              label: p.title || host.t('page.untitled'),
              icon: host.PageIcon ? (
                <host.PageIcon icon={p.icon} fallback={<FileText size={16} />} />
              ) : (
                (p.icon ?? <FileText size={16} />)
              ),
              run: () => insertLink(editor, range(), p),
            }));
            const exact = items.some((i) => i.label.toLowerCase() === q.trim().toLowerCase());
            if (q.trim() && !exact) {
              items.push({
                id: '__create',
                label: host.t('editor.createPage', { title: q.trim() }),
                icon: <FilePlus2 size={16} />,
                run: () => {
                  const r = range();
                  void host.createPage(q.trim()).then((p) => insertLink(editor, r, p));
                },
              });
            }
            return items;
          },
          render: menu(host),
        }),
      ];
    },
  });
}

// ---- #tags -----------------------------------------------------------------------

const TAG_CHARS = '[\\p{L}\\p{N}_\\-/]';

function insertTag(editor: Editor, range: Range, tag: string) {
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent([
      { type: 'hashtag', attrs: { tag } },
      { type: 'text', text: ' ' },
    ])
    .run();
}

export function createTagSuggest(host: EditorHost) {
  return Extension.create({
    name: 'tagSuggest',
    addProseMirrorPlugins() {
      return [
        Suggestion<MenuItem>({
          editor: this.editor,
          pluginKey: new PluginKey('tagSuggest'),
          char: '#',
          allowedPrefixes: SPACES,
          minQueryLength: 1,
          allow: ({ state, range }) => !state.doc.resolve(range.from).parent.type.spec.code,
          items: ({ query, editor }) => {
            if (!new RegExp(`^${TAG_CHARS}+$`, 'u').test(query) || !/[^\d/]/.test(query)) return [];
            const range = () => ({
              from: editor.state.selection.from - query.length - 1,
              to: editor.state.selection.from,
            });
            const tags = host.listTags(query).slice(0, 8);
            if (!tags.some((t) => t.toLowerCase() === query.toLowerCase())) tags.unshift(query);
            return tags.map((tag) => ({
              id: tag,
              label: `#${tag}`,
              icon: <Hash size={16} />,
              run: () => insertTag(editor, range(), tag),
            }));
          },
          render: menu(host),
        }),
      ];
    },
  });
}

/**
 * Typing `#tag ` or `[[Page]]` without using the popup still produces real
 * nodes — otherwise the text would export as an escaped `\#tag`.
 */
export function createTypingRules(host: EditorHost) {
  return Extension.create({
    name: 'nexusTypingRules',
    addInputRules() {
      return [
        new InputRule({
          find: new RegExp(`(?:^|\\s)#(${TAG_CHARS}*[^\\d\\s/#]${TAG_CHARS}*)\\s$`, 'u'),
          handler: ({ state, range, match }) => {
            const tag = match[1]!.replace(/\/+$/, '');
            const start = range.to - match[0].trimStart().length;
            const $start = state.doc.resolve(start);
            if ($start.parent.type.spec.code) return null;
            const schema = state.schema;
            state.tr.replaceWith(start, range.to, [
              schema.nodes.hashtag!.create({ tag }),
              schema.text(' '),
            ]);
          },
        }),
        new InputRule({
          find: /(!?)\[\[([^[\]\n|#]+)(?:#([^[\]\n|]+))?(?:\|([^[\]\n]+))?\]\]$/,
          handler: ({ state, range, match }) => {
            const [, bang, target, heading, alias] = match;
            const page = host.findPageByName(target!.trim());
            const attrs = {
              pageId: page?.id ?? null,
              target: page?.title ?? target!.trim(),
              heading: heading?.trim() || null,
            };
            const schema = state.schema;
            if (bang) {
              state.tr.replaceWith(range.from, range.to, schema.nodes.embed!.create(attrs));
            } else {
              state.tr.replaceWith(
                range.from,
                range.to,
                schema.nodes.wikiLink!.create({ ...attrs, alias: alias?.trim() || null }),
              );
            }
          },
        }),
      ];
    },
  });
}
